import {jsx} from 'panel';

import {SelectEl} from 'common/components/select';
import {EntryPart} from 'common/widgets/query-entry-section/types';
import {DateRangePickerEl} from 'common/widgets/date-range-picker-new';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DateRangeType, DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {DateRangePickerApplyEvent} from 'common/widgets/date-range-picker-new/types';

import type {FilterEntryRow} from '../index';

export function FilterDateValue(this: FilterEntryRow) {
  const {activeEntry, entryPart} = this.section.state;
  const isOpen = activeEntry === this.index && entryPart === EntryPart.FilterValue;

  const [, , ...labelParts] = this.filter.getLabelParts();
  const label = labelParts.map((part) => part.label).join(` `);

  /**
   * All possible date flters are expressed as a relative or absolute date selection
   * we express the date filter operator in terms of only these 3 primitives
   */
  const selectedDate = (this.filter.filterValue as DateRange).getAttributes();
  switch (selectedDate.type) {
    case DateRangeType.NotOn:
      selectedDate.type = DateRangeType.On;
      break;
    case DateRangeType.NotBetween:
      selectedDate.type = DateRangeType.Between;
      break;
    case DateRangeType.RelativeBefore:
    case DateRangeType.NotRelativeAfter:
      selectedDate.type = DateRangeType.RelativeAfter;
      break;
    case DateRangeType.Before:
      selectedDate.type = DateRangeType.On;
      selectedDate.from = selectedDate.to;
      break;
  }

  /**
   * TODO: date filters can all be expressed with the following operators:
   *
   * ==, !=, >=, ∋, ∌, <=
   * = (in the last, on)
   * != (not in the last, not on)
   * > (in the next, since)
   * ∋ (between)
   * ∌ (not between)
   * < (before the last, ending)
   *
   * These require additional changes to the API:
   *  - [!= last x days] not in the last: translates to not between
   *  - [< date] before date: translates to "ending at date"
   */
  const handleDatePickerChange = (dateRange: DateRange) => {
    const {filterOperator} = this.filter;
    if (filterOperator === DateRangeType.Before) {
      dateRange.from = null;
    }

    const filter = this.filter.clonePropertyFilter({
      filterValue: this.filter.createDateRange({
        ...dateRange.getAttributes(),
        type: this.filter.filterOperator as DateRangeType,
      }),
    });

    this.updateEntry(filter);
  };

  return (
    <SelectEl
      class={{
        'is-placeholder': !this.filter.isValid(),
      }}
      attrs={{
        elref: `filterValueSelect`,
        open: isOpen,
        theme: `select`,
        'selected-option': this.filter.isValid() ? label : `Select value...`,
      }}
      on={{
        menuChange: (ev) => this.section.helpers.onSelectMenuChange(ev, this.index, EntryPart.FilterValue),
      }}
    >
      {isOpen && (
        <DateRangePickerEl
          attrs={{
            elref: `filterDateRangePicker`,
            'single-picker-mode': true,
            'display-context': DisplayContext.PropertyFilter,
            slot: `body`,
            'date-range': this.filter.isValid() && selectedDate,
            'tracked-use-case': `filters`,
          }}
          on={{
            apply: (ev: DateRangePickerApplyEvent) => handleDatePickerChange(ev.detail.dateRange),
            cancel: () => this.section.helpers.setActiveEntry(`close`, this.index, EntryPart.FilterValue),
          }}
        />
      )}
    </SelectEl>
  );
}
